import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MapIcon from "@mui/icons-material/Map";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import api, { isCancel } from "../../services/api/axios";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import {
  ACTIONS_HEADER_BTN_SX,
  ACTION_MENU_PAPER_SX,
} from "../../theme/buttonstyles";
import GeographicTargetingMap from "./GeographicTargetingMap";
import {
  buildIndexes,
  calculateRecommendations,
  criteriaChips,
  formatNumber,
  getModeConfig,
  initialLayers,
  interpretTargetingPrompt,
  labelGeo,
  resolveCriteria,
  toRecommendationCsv,
} from "./geographicTargetingUtils";
import "./geographicTargeting.css";

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
  background: "#ffffff",
};


export default function GeographicTargeting() {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("state");
  const [selectedNaics, setSelectedNaics] = useState("");
  const [anchors, setAnchors] = useState(() => new Set());
  const [layers, setLayers] = useState(initialLayers);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiCriteria, setAiCriteria] = useState(null);
  const [aiStatus, setAiStatus] = useState("");
  const [aiStatusType, setAiStatusType] = useState("default");
  const actionsMenuOpen = Boolean(actionsAnchorEl);

  const fetchData = useCallback(async (signal) => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/geographic-targeting/data", { signal });
      if (signal?.aborted) return;
      setData(response.data);
      const industries = response.data.all_industries || response.data.industries || [];
      setSelectedNaics((current) => industries.some((industry) => industry.naics === current)
        ? current : industries[0]?.naics || "");
    } catch (err) {
      if (isCancel(err) || signal?.aborted) return;
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to load geographic targeting data.",
      );
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const indexes = useMemo(() => (data ? buildIndexes(data) : null), [data]);
  const modeDetails = useMemo(() => getModeConfig(mode), [mode]);

  const recommendations = useMemo(() => {
    if (!data || !indexes || !selectedNaics) return [];
    return calculateRecommendations({
      data,
      indexes,
      mode,
      selectedNaics,
      anchors,
      layers,
      criteria: aiCriteria,
    });
  }, [aiCriteria, anchors, data, indexes, layers, mode, selectedNaics]);

  const selectedIndustry = useMemo(
    () => {
      const industries = data?.all_industries || data?.industries || [];
      return industries.find((industry) => industry.naics === selectedNaics);
    },
    [data?.all_industries, data?.industries, selectedNaics],
  );

  const industryOptions = useMemo(
    () => data?.all_industries || data?.industries || [],
    [data?.all_industries, data?.industries],
  );

  const anchorStateOptions = useMemo(
    () => [...(data?.states || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [data?.states],
  );

  const selectedAnchorRows = useMemo(() => {
    const geoMap = indexes?.[modeDetails.indexKey];
    return [...anchors].map((id) => geoMap?.get(id)).filter(Boolean);
  }, [anchors, indexes, modeDetails.indexKey]);

  const aiChips = useMemo(
    () => criteriaChips(aiCriteria, industryOptions),
    [aiCriteria, industryOptions],
  );

  const applyAiCriteria = (criteria) => {
    const nextMode = criteria.level || mode;
    const config = getModeConfig(nextMode);
    const nextGeoRows = data?.[config.geoKey] || [];
    const { anchorIds, unmatchedAnchors } = resolveCriteria(criteria, nextGeoRows, nextMode);
    const applied = {
      ...criteria,
      unmatchedAnchors,
    };

    if (criteria.level && criteria.level !== mode) {
      setMode(criteria.level);
    }
    if (criteria.naics && industryOptions.some((industry) => industry.naics === criteria.naics)) {
      setSelectedNaics(criteria.naics);
    }
    if (anchorIds.length) setAnchors(new Set(anchorIds));
    setAiCriteria(applied);

    const parts = ["Re-ranked targets"];
    if (!criteria.naics) parts.push("kept the current industry");
    if (unmatchedAnchors.length) parts.push(`couldn't place ${unmatchedAnchors.join(", ")}`);
    setAiStatus(parts.join(" - "));
    setAiStatusType(unmatchedAnchors.length ? "warning" : "success");
  };

  const handleAiRun = () => {
    const text = aiPrompt.trim();
    if (!text) {
      setAiStatus("Describe what you are looking for first.");
      setAiStatusType("error");
      return;
    }
    const criteria = interpretTargetingPrompt(text, {
      industries: industryOptions,
      stateNames: anchorStateOptions.map((state) => state.name),
    });
    applyAiCriteria(criteria);
  };

  const handleAiClear = () => {
    setAiPrompt("");
    setAiCriteria(null);
    setAiStatus("");
    setAiStatusType("default");
  };

  const handleDropAiCriterion = (key) => {
    setAiCriteria((current) => {
      if (!current) return current;
      const next = { ...current };
      if (key === "level") next.level = null;
      else if (key === "naics") next.naics = null;
      else if (key === "states") next.stateNames = [];
      else if (key === "anchor") {
        next.anchorNames = [];
        setAnchors(new Set());
      } else if (key === "emphasis") {
        next.emphasis = null;
        next.weights = null;
      } else if (key === "minLq") next.minLq = null;
      else if (key === "maxPop") next.maxPopulation = null;
      else if (key === "minPop") next.minPopulation = null;
      return next;
    });
  };

  const handleToggleAnchor = useCallback((id) => {
    setAnchors((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleExport = () => {
    const { filename, content } = toRecommendationCsv(recommendations, mode, selectedNaics);
    const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <Box
      className="geographic-targeting-page"
      sx={{
        backgroundColor: "#f7f9fc",
        minHeight: "100%",
        color: BRAND_BLUE,
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: BRAND_GREEN,
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <MapIcon sx={{ fontSize: 19 }} />
            </Box>
            <Typography
              sx={{
                fontSize: "1.05rem",
                fontWeight: 800,
                color: BRAND_BLUE,
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
              }}
            >
              Geographic Targeting
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(event) => setActionsAnchorEl(event.currentTarget)}
            sx={ACTIONS_HEADER_BTN_SX}
          >
            Actions
          </Button>

          <Menu
            anchorEl={actionsAnchorEl}
            open={actionsMenuOpen}
            onClose={() => setActionsAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                ...ACTION_MENU_PAPER_SX,
                "& .MuiMenuItem-root": {
                  ...ACTION_MENU_PAPER_SX["& .MuiMenuItem-root"],
                  color: BRAND_BLUE,
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setActionsAnchorEl(null);
                fetchData();
              }}
              disabled={loading}
            >
              <ListItemIcon sx={{ minWidth: 32, color: BRAND_BLUE }}>
                <RefreshIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Refresh</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setActionsAnchorEl(null);
                handleExport();
              }}
              disabled={!recommendations.length}
            >
              <ListItemIcon sx={{ minWidth: 32, color: BRAND_BLUE }}>
                <FileDownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export to CSV</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>
          {loading && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && data && (
            <>
            <Box className="geographic-targeting-layout">
              <Paper sx={{ ...CARD_SX, p: 1.5, position: "relative", overflow: "hidden" }}>
                <GeographicTargetingMap
                  data={data}
                  indexes={indexes}
                  mode={mode}
                  selectedNaics={selectedNaics}
                  anchors={anchors}
                  layers={layers}
                  recommendations={recommendations}
                  onToggleAnchor={handleToggleAnchor}
                  onSetMode={setMode}
                  onSetSelectedNaics={setSelectedNaics}
                  onSetAnchors={setAnchors}
                  onSetLayers={setLayers}
                />
                <Box className="geographic-targeting-legend" aria-label="Map key">
                  <Box className="geographic-targeting-legend-header">
                    <Typography component="h3">Map key</Typography>
                    <span>Rank & overlays</span>
                  </Box>
                  <Box className="geographic-targeting-rank-key">
                    <Box className="geographic-targeting-rank-ramp" />
                    <Box className="geographic-targeting-rank-labels">
                      <span>Best</span>
                      <span>Middle</span>
                      <span>Lower</span>
                    </Box>
                  </Box>
                  <Box className="geographic-targeting-legend-items">
                    <span>
                      <i className="circle" style={{ background: "#14b895" }} />
                      LQ 1.25+
                    </span>
                    <span>
                      <i className="outline" style={{ borderColor: "#111827" }} />
                      Anchor
                    </span>
                    <span>
                      <i className="outline recommend" />
                      Recommendation
                    </span>
                  </Box>
                </Box>
              </Paper>
            </Box>

            <Paper sx={{ ...CARD_SX, overflow: "hidden" }}>
              <Box
                sx={{
                  p: 2,
                  borderBottom: "1px solid #d8e0ea",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: BRAND_BLUE }}>
                    Ranked Output
                  </Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: BRAND_BLUE }}>
                    {modeDetails.label} Recommendations
                    {selectedIndustry ? ` for ${selectedIndustry.label} (${selectedIndustry.naics})` : ""}
                  </Typography>
                </Box>
                {selectedAnchorRows.length > 0 && (
                  <Typography sx={{ color: BRAND_BLUE, fontSize: 13, maxWidth: 560 }}>
                    {`Anchors: ${selectedAnchorRows
                      .map((row) => `${mode === "metro" ? row.name : labelGeo(row)} (#${row.rank})`)
                      .join("; ")}`}
                  </Typography>
                )}
                <Box sx={{ flexBasis: "100%", mt: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: 1.25 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 1.5,
                        display: "grid",
                        placeItems: "center",
                        background: BRAND_BLUE,
                        color: "#ffffff",
                        flex: "0 0 auto",
                      }}
                    >
                      <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                      <Typography sx={{ color: BRAND_BLUE, fontSize: 16, fontWeight: 800 }}>
                        Describe what you're looking for
                      </Typography>
                    </Box>
                  </Box>

                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    value={aiPrompt}
                    onChange={(event) => setAiPrompt(event.target.value)}
                    placeholder="e.g. We make composite boat hulls and want Southern metros where the industry is growing but no one dominates yet - we're already in Charleston."
                    sx={{
                      "& .MuiInputBase-root": {
                        backgroundColor: "#ffffff",
                      },
                    }}
                  />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.25, flexWrap: "wrap" }}>
                    <Button
                      className="geographic-targeting-ai-primary"
                      variant="contained"
                      onClick={handleAiRun}
                    >
                      Re-rank targets
                    </Button>
                    <Button variant="text" onClick={handleAiClear}>
                      Clear
                    </Button>
                    {aiStatus && (
                      <Typography
                        sx={{
                          color:
                            aiStatusType === "error"
                              ? "#b91c1c"
                              : aiStatusType === "warning"
                                ? "#9a6a00"
                                : BRAND_BLUE,
                          fontSize: 12,
                        }}
                      >
                        {aiStatus}
                      </Typography>
                    )}
                  </Box>

                  {aiChips.length > 0 && (
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1.25 }}>
                      {aiChips.map((chip) => (
                        <Chip
                          key={`${chip.key}-${chip.label}`}
                          label={chip.label}
                          onDelete={() => handleDropAiCriterion(chip.key)}
                          size="small"
                          sx={{
                            color: BRAND_BLUE,
                            borderColor: "#c6dbf0",
                            fontWeight: 700,
                            background: "#ffffff",
                          }}
                          variant="outlined"
                        />
                      ))}
                      {aiCriteria?.rationale && (
                        <Typography
                          sx={{
                            flexBasis: "100%",
                            color: BRAND_BLUE,
                            fontSize: 12,
                            opacity: 0.78,
                          }}
                        >
                          {aiCriteria.rationale}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              <Box sx={{ p: 1.75 }}>
                {!recommendations.length ? (
                  <Alert severity="info">
                    No recommendations match the current NAICS and layer filters. Try another
                    industry or clear anchor-only targeting.
                  </Alert>
                ) : (
                  <Stack spacing={1.25}>
                    {recommendations.slice(0, modeDetails.topListSize).map((row, index) => (
                      <Box className="geographic-targeting-result" key={row.key}>
                        <Box className="geographic-targeting-result-rank">{index + 1}</Box>
                        <Box>
                          <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>
                            {mode === "metro" ? row.name : labelGeo(row)} {mode === "state" ? `(${row.abbr})` : ""}
                          </Typography>
                          <Typography sx={{ color: BRAND_BLUE, fontSize: 13, mt: 0.5 }}>
                            {row.reason}
                          </Typography>
                          <Box className="geographic-targeting-badges">
                            <span>PE Rank #{row.rank}</span>
                            <span>Score {row.score}</span>
                            <span className={row.industry.lq >= 1.25 ? "green" : "orange"}>
                              LQ {row.industry.lq}
                            </span>
                            <span>{formatNumber.format(row.industry.emp)} Employees</span>
                            <span>{formatNumber.format(row.industry.estab)} Establishments</span>
                            {row.rankGap > 0 && (
                              <span className="orange">+{formatNumber.format(row.rankGap)} Rank Gap</span>
                            )}
                          </Box>
                        </Box>
                        <Box className="geographic-targeting-score">{row.recScore}</Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Paper>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
